import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineBriefcase, HiOutlineUsers, HiSparkles, HiMagnifyingGlass } from 'react-icons/hi2';
import '../styles/OutreachNavigator.css';
import * as Sentry from '@sentry/react';

function OutreachNavigator({
    isOpen,
    onClose,
    onOpenCoPilot,
    job,
    user,
    hasMessage,
    setErrorMessage,
    setShowErrorPopup
}) {
    const [showCoPilot, setShowCoPilot] = useState(false);
    const [linkedinSearchInfo, setLinkedinSearchInfo] = useState(null);
    const linkedinSearchPromiseRef = useRef(null);

    const fetchLinkedinSearchInfo = async () => {
        if (!job?.id || !user?.reported_college) return null;

        // If a request is already in flight, return that same promise
        if (linkedinSearchPromiseRef.current) {
            return linkedinSearchPromiseRef.current;
        }

        linkedinSearchPromiseRef.current = (async () => {
            try {
                const params = new URLSearchParams({
                    school_name: user.reported_college,
                    company_id: String(job.company_id)
                });

                const response = await fetch(`/api/get_linkedin_search_info?${params.toString()}`);
                const data = await response.json();

                if (data.status === 'session_fail') {
                    window.location.href = '/results';
                    return null;
                }

                if (data.status === 'success') {
                    setLinkedinSearchInfo(data);
                    return data;
                } else {
                    setErrorMessage(data.error_message || "Failed to retrieve LinkedIn search info. Please reload and try again.");
                    setShowErrorPopup(true);
                    setTimeout(() => setShowErrorPopup(false), 3000);
                    setLinkedinSearchInfo(null);
                    return null;
                }
            } catch (error) {
                setErrorMessage("Failed to retrieve LinkedIn search info. Please reload and try again.");
                setShowErrorPopup(true);
                setTimeout(() => setShowErrorPopup(false), 3000);
                Sentry.captureException(error);
                setLinkedinSearchInfo(null);
                return null;
            } finally {
                linkedinSearchPromiseRef.current = null;
            }
        })();

        return linkedinSearchPromiseRef.current;
    };

    // Reset state when component opens / job changes
    useEffect(() => {
        if (isOpen) {
            setShowCoPilot(false);
            setLinkedinSearchInfo(null);
            linkedinSearchPromiseRef.current = null;
        }
    }, [isOpen, job?.id, user?.reported_college]);

    // Start fetch as soon as modal opens
    useEffect(() => {
        if (isOpen && job?.id && user?.reported_college) {
            fetchLinkedinSearchInfo();
        }
    }, [isOpen, job?.id, user?.reported_college]);

    // Generate LinkedIn URLs using better search approaches
    const generateLinkedInURL = (searchType, searchInfoOverride = null) => {
        if (!job || !user) {
            console.error('Missing job or user data:', { job, user });
            return 'https://www.linkedin.com/';
        }

        const searchInfo = searchInfoOverride || linkedinSearchInfo;

        const schoolNameFallback = (user.reported_college || '').trim();
        const companyNameFallback = (job.company_name || '').trim();

        const schoolLinkedinId = searchInfo?.school_linkedin_id || null;
        const schoolName = (searchInfo?.school_name || schoolNameFallback || '').trim();

        const companyLinkedinId = searchInfo?.company_linkedin_id || null;
        const companyNameOriginal = (searchInfo?.company_name_original || companyNameFallback || '').trim();
        const companyNameCleaned = (searchInfo?.company_name_cleaned || '').trim();

        console.log('LinkedIn URL generation:', {
            searchType,
            schoolLinkedinId,
            schoolName,
            companyLinkedinId,
            companyNameOriginal,
            companyNameCleaned,
            searchInfo,
            job,
            user
        });

        const base = 'https://www.linkedin.com/search/results/people/';

        if (searchType === 'alumni') {
            const params = new URLSearchParams();
            params.set('origin', 'FACETED_SEARCH');

            // Prefer company LinkedIn ID, otherwise use company name(s)
            if (companyLinkedinId) {
                params.set('currentCompany', JSON.stringify([companyLinkedinId]));
            } else {
                const companyKeywords = [companyNameOriginal, companyNameCleaned]
                    .filter(Boolean)
                    .filter((value, index, arr) => arr.indexOf(value) === index)
                    .map(name => `"${name}"`)
                    .join(' OR ');

                if (companyKeywords) {
                    params.set('keywords', companyKeywords);
                }
            }

            // Prefer school LinkedIn ID, otherwise use school name
            if (schoolLinkedinId) {
                const schoolIds = Array.isArray(schoolLinkedinId) ? schoolLinkedinId : [schoolLinkedinId];
                params.set('schoolFilter', JSON.stringify(schoolIds));
            } else if (schoolName) {
                params.set('schoolFreetext', `"${schoolName}"`);
            }

            const finalURL = `${base}?${params.toString()}`;
            console.log('Generated alumni search URL:', finalURL);
            return finalURL;
        }

        if (searchType === 'recruiters') {
            const recruiterTitles = ['Recruiter', 'Talent Acquisition', 'University Recruiting'];
            const titleQuery = recruiterTitles.map(t => `"${t}"`).join(' OR ');

            const params = new URLSearchParams();
            params.set('origin', 'FACETED_SEARCH');

            // Prefer company LinkedIn ID, otherwise use company name(s)
            if (companyLinkedinId) {
                params.set('currentCompany', JSON.stringify([companyLinkedinId]));
                params.set('keywords', titleQuery);
            } else {
                const companyKeywords = [companyNameOriginal, companyNameCleaned]
                    .filter(Boolean)
                    .filter((value, index, arr) => arr.indexOf(value) === index)
                    .map(name => `"${name}"`)
                    .join(' OR ');

                const fullKeywordQuery = [titleQuery, companyKeywords]
                    .filter(Boolean)
                    .join(' AND ');

                if (fullKeywordQuery) {
                    params.set('keywords', fullKeywordQuery);
                }
            }

            const finalURL = `${base}?${params.toString()}`;
            console.log('Generated recruiter search URL:', finalURL);
            return finalURL;
        }

        return 'https://www.linkedin.com/';
    };

    const handleCardClick = async (searchType) => {
        try {
            let searchInfo = linkedinSearchInfo;

            // If info is not ready yet, wait for the already in-flight request
            if (!searchInfo) {
                searchInfo = await fetchLinkedinSearchInfo();
            }

            const linkedinURL = generateLinkedInURL(searchType, searchInfo);

            window.open(linkedinURL, '_blank');

            fetch('/api/increment_linkedin_clicks', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            })
                .then(res => res.json())
                .then(data => {
                    if (data.status !== 'success') {
                        setErrorMessage(data.error_message || 'Something went wrong.');
                        setShowErrorPopup(true);
                        setTimeout(() => setShowErrorPopup(false), 3000);
                    }
                })
                .catch(err => {
                    setErrorMessage('Something went wrong.');
                    setShowErrorPopup(true);
                    setTimeout(() => setShowErrorPopup(false), 3000);
                    Sentry.captureException(err);
                });

            setTimeout(() => {
                setShowCoPilot(true);
            }, 1000);
        } catch (error) {
            setErrorMessage('Something went wrong.');
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 3000);
            Sentry.captureException(error);
        }
    };

    if (!isOpen) {
        return null;
    }

    if (showCoPilot) {
        return (
            <div className="navigator-overlay">
                <div className="navigator-modal">
                    <div className="modal-header">
                        <h2>✨ Ready to Draft Your Message?</h2>
                        <button className="close-btn" style={{ color: 'black' }} onClick={onClose}>×</button>
                    </div>

                    <div className="copilot-intro">
                        <p>Now that you've found the right people to connect with, let's craft a personalized message that stands out.</p>

                        <div className="copilot-cta">
                            <button
                                className="big-copilot-btn"
                                onClick={() => {
                                    onOpenCoPilot?.(job);
                                }}
                            >
                                <HiSparkles size={16} style={{ marginRight: '6px' }} />
                                Draft Message with AI Co-Pilot
                            </button>
                        </div>

                        <p className="copilot-subtitle">
                            Our AI will analyze your resume and this job posting to create a highly personalized outreach message.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="navigator-overlay">
            <div className="navigator-modal">
                <div className="modal-header">
                    <h2>
                        <HiMagnifyingGlass size={24} style={{ display: 'inline', marginRight: '8px' }} />
                        LinkedIn Navigator
                    </h2>
                    <button className="close-btn" style={{ color: 'black' }} onClick={onClose}>×</button>
                </div>

                <div className="navigator-cards">
                    <div
                        className="navigator-card alumni"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleCardClick('alumni')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleCardClick('alumni');
                            }
                        }}
                    >
                        <div className="card-icon">
                            <HiOutlineUsers size={32} />
                        </div>
                        <h3>School Alumni</h3>
                        <p className="card-subtitle">Find {user?.reported_college} Alumni</p>
                        <p className="card-description">
                            Connect with {user?.reported_college} graduates who work at {job?.company_name}.
                        </p>
                    </div>

                    <div
                        className="navigator-card recruiters"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleCardClick('recruiters')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleCardClick('recruiters');
                            }
                        }}
                    >
                        <div className="card-icon">
                            <HiOutlineBriefcase size={32} />
                        </div>
                        <h3>Company Recruiters</h3>
                        <p className="card-subtitle">Find Recruiters</p>
                        <p className="card-description">
                            Connect with recruiters and talent acquisition specialists at {job?.company_name}.
                        </p>
                    </div>
                </div>

                <div className="navigator-footer">
                    <p>
                        <strong>Alumni:</strong> Search for {user?.reported_college} graduates at {job?.company_name}.<br />
                        <strong>Recruiters:</strong> Find hiring managers and talent acquisition specialists.
                    </p>
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
                        💡 Note: You'll need to sign into LinkedIn to see search results.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default OutreachNavigator;